/**
 * The model picker (M-REB, Pedro 2026-10-26: Rebalance to… "can open a choice with buttons: Model
 * and Portfolio, which then open either a Model picker or a Portfolio picker, similar to the
 * existing one"). The single-choice look of `PortfolioPicker`: a search box, the models with a
 * radio (a model with a not-approved member flagged red), and an apply button + Cancel. Opened
 * by the caller, anchored to `target`.
 *
 * RB-DEFAULT (Pedro 2026-10-29): it opens with the default model chosen (`defaultId`); choosing
 * another and clicking the action shows a loud red warning naming both models and asks to
 * confirm before anything happens ("a loud warning and a request to confirm before proceeding").
 */
import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Group, Popover, Radio, ScrollArea, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';

import { modelsApi } from '../api/models';

export function ModelPicker({ opened, onClose, onChoose, target, intro, action, busy, defaultId, defaultNote }: {
  opened: boolean; onClose: () => void; onChoose: (id: string, name: string) => void;
  target: React.ReactNode; intro?: string; action: string; busy?: boolean;
  /** RB-DEFAULT (Pedro 2026-10-29): the assigned model, chosen when the picker opens; changeable. */
  defaultId?: string | null;
  /** One line saying where the default comes from (or why there is none). */
  defaultNote?: string | null;
}) {
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  useEffect(() => { if (opened) { setChosen(defaultId ?? null); setSearch(''); setConfirming(false); } }, [opened, defaultId]);
  useEffect(() => { setConfirming(false); }, [chosen]);
  const q = useQuery({ queryKey: ['models', 'models'], queryFn: () => modelsApi.models(), enabled: opened });
  const t = search.trim().toLowerCase();
  const items = (q.data?.items ?? []).filter((m) => !t || m.name.toLowerCase().includes(t));
  const pick = items.find((m) => m.id === chosen) ?? (q.data?.items ?? []).find((m) => m.id === chosen);
  const defaultName = defaultId ? (q.data?.items ?? []).find((m) => m.id === defaultId)?.name ?? null : null;
  return (
    <Popover width={320} position="bottom-start" shadow="md" withArrow opened={opened}
      onChange={(o) => { if (!o) onClose(); }} trapFocus>
      <Popover.Target>{target}</Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          {intro && <Text size="xs" c="dimmed">{intro}</Text>}
          {defaultNote && <Text size="xs" c="blue">{defaultNote}</Text>}
          <TextInput size="xs" placeholder="Model name" value={search} onChange={(e) => setSearch(e.currentTarget.value)} />
          <ScrollArea.Autosize mah={300}>
            <Stack gap={4}>
              {items.length === 0 && <Text size="xs" c="dimmed">{q.isLoading ? 'Loading…' : 'No model (create one in Admin → Models)'}</Text>}
              {items.map((m) => (
                <Group key={m.id} gap={6} wrap="nowrap">
                  <Radio size="xs" label={m.name} checked={chosen === m.id} onChange={() => setChosen(m.id)} />
                  {m.flagged.length > 0 && (
                    <Tooltip withArrow multiline w={280} label={`Bucket(s) with a member no longer Approved: ${m.flagged.join(', ')}.`}>
                      <Badge size="xs" color="red" variant="light">not approved</Badge>
                    </Tooltip>
                  )}
                </Group>
              ))}
            </Stack>
          </ScrollArea.Autosize>
          <Text size="xs" c="dimmed">{pick ? pick.name : 'Choose one'}</Text>
          {confirming && pick ? (
            // Fits the picker's width (Pedro's smoke, 2026-10-29: the long button label clipped it):
            // short labels, buttons stacked full width, text allowed to wrap.
            <Alert color="red" variant="filled" icon={<IconAlertTriangle size={18} />} title="Not the default model" p="xs"
              styles={{ body: { minWidth: 0 }, message: { overflowWrap: 'anywhere' } }}>
              <Stack gap={6}>
                <Text size="xs">
                  {pick.name} is not the default model ({defaultName ?? 'the default'}). {action} to {pick.name} anyway?
                </Text>
                <Button size="xs" color="red" variant="white" fullWidth disabled={!!busy}
                  onClick={() => { onChoose(pick.id, pick.name); onClose(); }}>{action} anyway</Button>
                <Button size="xs" variant="default" fullWidth onClick={() => setConfirming(false)}>Back</Button>
              </Stack>
            </Alert>
          ) : (
            <Group gap="xs" justify="flex-end">
              <Button size="xs" disabled={!pick || !!busy} onClick={() => {
                if (!pick) return;
                if (defaultId && pick.id !== defaultId) { setConfirming(true); return; }
                onChoose(pick.id, pick.name); onClose();
              }}>{action}</Button>
              <Button size="xs" variant="default" onClick={onClose}>Cancel</Button>
            </Group>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
